import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components/macro";
import {
  CONSTANTS,
  COLORS,
  FONTWEIGHT,
  FONTSIZE
} from "helpers/designConstants";

import { useDatabaseReader } from "database/useDatabaseReader";
import useCatalogueSearchReader from "database/catalogues/useCatalogueSearchReader";
import useCatalogueWriter from "database/catalogues/useCatalogueWriter";
import useArticleSearchReader from "database/articles/useArticleSearchReader";

import Loading from "components/shared/Loading";
import ScrollArea from "components/shared/ScrollArea";
import SearchBar from "components/shared/SearchBar";
import { Button, MiniButton } from "components/shared/standardElements";
import CreateEditCatalogueModal from "./CreateEditCatalogueModal";
import CatalogueCard from "./CatalogueCard";
import CatalogueProductPickerModal from "./catalogueProductPickerModal/CatalogueProductPickerModal";
import CatalogueSectionBuilder from "./catalogueSectionBuilder/CatalogueSectionBuilder";
import { copyToClipboard } from "helpers/copyToClipboard";
import { toast } from "react-toastify";
import ConfirmModal from "components/shared/ConfirmModal";
import {
  updateSectionById,
  stripArticleIDsFromSections,
  buildSectionNamesByArticleID
} from "helpers/sectionTreeHelpers";
import {
  normalizeArticleEntries,
  extractArticleIDs,
  buildSeasonMap
} from "helpers/catalogueArticleHelpers";
import { cloneSectionTree } from "helpers/cloneSectionTree";

// Tracks the page session so we can tell a fresh page load from a tab-switch
// remount.  performance.timeOrigin is unique per page load; it resets on refresh.
let lastSeenPageLoad = null;

const sortCatalogues = (catalogues, sortBy) => {
  if (sortBy === "date") {
    return catalogues.sort((a, b) =>
      a.creationDate < b.creationDate
        ? 1
        : a.creationDate > b.creationDate
        ? -1
        : 0
    );
  } else if (sortBy === "alpha") {
    return catalogues.sort((a, b) =>
      a.name.toLowerCase() > b.name.toLowerCase()
        ? 1
        : a.name.toLowerCase() < b.name.toLowerCase()
        ? -1
        : 0
    );
  }
};

const CatalogueCreatorTab = () => {
  //HOOKS
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { cataloguesSearchAPIKey, getCatalogueByID } = useDatabaseReader();
  const { searchForCatalogue } = useCatalogueSearchReader();
  const {
    createNewCatalogue,
    updateCatalogueDetails,
    updateCatalogueArticleIDs,
    updateCatalogueCustomSections,
    updateCatalogueSectionsAndArticles,
    deleteCatalogue
  } = useCatalogueWriter();
  const { retrieveArticlesByIDs } = useArticleSearchReader();
  const didRestoreBuilder = useRef(false);
  const isFirstEffectRun = useRef(true);

  //STATE
  const [isLoading, setIsLoading] = useState(false);
  const [sortBy, setSortBy] = useState("date");
  const [showCreateCatalogueModal, setShowCreateCatalogueModal] =
    useState(false);
  const [catalogueToEdit, setCatalogueToEdit] = useState();
  const [catalogueForProductPickerModal, setCatalogueForPrductPickerModal] =
    useState();
  const [catalogueToDelete, setCatalogueToDelete] = useState();
  const [filteredCatalogues, setFilteredCatalogues] = useState([]);

  // Section builder state
  const [catalogueForSectionBuilder, setCatalogueForSectionBuilder] =
    useState();
  const [sectionBuilderSections, setSectionBuilderSections] = useState([]);
  const [sectionBuilderArticles, setSectionBuilderArticles] = useState([]);
  const [catalogueEntries, setCatalogueEntries] = useState([]);
  const [sectionForNewArticles, setSectionForNewArticles] = useState(null);

  useEffect(() => {
    if (cataloguesSearchAPIKey == null) return;
    searchForCatalogue("*").then((result) => {
      const sortedResult = sortCatalogues(result, sortBy);
      setFilteredCatalogues(sortedResult);
    });
  }, [searchForCatalogue, cataloguesSearchAPIKey, sortBy]);

  // Restore builder from URL param on page load only.
  // When the user switches tabs and comes back the component remounts, but we
  // detect that via lastSeenPageLoad and strip the stale builderId instead of
  // re-opening the builder.
  useEffect(() => {
    // Only check for tab-switch remount on the first effect run per mount.
    // Subsequent runs (e.g. when the user opens the builder and builderId is
    // added to the URL) must fall through to the normal logic.
    if (isFirstEffectRun.current) {
      isFirstEffectRun.current = false;

      const isTabSwitchRemount =
        lastSeenPageLoad === performance.timeOrigin &&
        !didRestoreBuilder.current;

      if (isTabSwitchRemount) {
        const builderId = searchParams.get("builderId");
        if (builderId) {
          const params = new URLSearchParams(searchParams);
          params.delete("builderId");
          setSearchParams(params, { replace: true });
        }
        return;
      }

      lastSeenPageLoad = performance.timeOrigin;
    }

    if (didRestoreBuilder.current) return;
    const builderId = searchParams.get("builderId");
    if (!builderId || !getCatalogueByID) return;
    didRestoreBuilder.current = true;
    (async () => {
      const catalogue = await getCatalogueByID(builderId);
      if (!catalogue || catalogue.groupingMode !== "custom") return;
      setCatalogueForSectionBuilder(catalogue);
      setSectionBuilderSections(catalogue.customSections || []);
      const entries = normalizeArticleEntries(catalogue.articleIDs, catalogue.seasonKey);
      setCatalogueEntries(entries);
      if (catalogue.articleIDs?.length > 0) {
        const uniqueIDs = extractArticleIDs(entries);
        const articles = await retrieveArticlesByIDs(uniqueIDs);
        setSectionBuilderArticles(Object.values(articles));
      }
    })();
  }, [searchParams, getCatalogueByID, retrieveArticlesByIDs, setSearchParams]);

  //METHODS
  const onSearchBarChanged = async (searchTerm) => {
    searchForCatalogue(searchTerm).then((result) => {
      const sortedResult = sortCatalogues(result, sortBy);
      setFilteredCatalogues(sortedResult);
    });
  };

  const onCreateUpdateCatalogue = async (data) => {
    setShowCreateCatalogueModal(false);
    setIsLoading(true);
    if (catalogueToEdit == null) {
      const newCatalogue = await createNewCatalogue(data);

      // Apply template if selected
      if (data.templateCatalogueID) {
        const source = await getCatalogueByID(data.templateCatalogueID);
        if (source?.customSections?.length > 0) {
          const clearArticleIDs = data.templateMode !== "groups_and_articles";
          const clonedSections = cloneSectionTree(source.customSections, { clearArticleIDs });
          await updateCatalogueCustomSections(clonedSections, newCatalogue.id);
          newCatalogue.customSections = clonedSections;

          if (!clearArticleIDs && source.articleIDs?.length > 0) {
            await updateCatalogueArticleIDs(source.articleIDs, newCatalogue.id);
            newCatalogue.articleIDs = source.articleIDs;
          }
        }
      }

      setFilteredCatalogues((prev) =>
        sortCatalogues([...prev, newCatalogue], sortBy)
      );
    } else {
      //Update the local catalogue in this component
      //Done because this is not refetched from Typesense
      const catalogueIndex = filteredCatalogues.findIndex(
        (catalogue) => catalogue.id === catalogueToEdit.id
      );
      const updatedCatalogue = {
        ...filteredCatalogues[catalogueIndex],
        name: data.catalogueName,
        priceToShowPreview: data.priceToShowPreview,
        sortOrder: data.sortOrder,
        groupingMode: data.groupingMode || "schema",
        seasonKey: data.seasonOptions?.seasonKey || "",
        seasonName: data.seasonOptions?.seasonName || "",
        seasonName_fr: data.seasonOptions?.seasonName_fr || "",
        seasonSiloKey: data.seasonOptions?.siloKey || "",
        seasonSiloName: data.seasonOptions?.siloName || "",
        seasonSiloName_fr: data.seasonOptions?.siloName_fr || ""
      };
      const catalogues = [...filteredCatalogues];
      catalogues[catalogueIndex] = updatedCatalogue;
      setFilteredCatalogues(catalogues);

      //Update Firebase Catalogue details which will updated Typesense
      await updateCatalogueDetails(data, catalogueToEdit.id);
      setCatalogueToEdit();
    }
    setIsLoading(false);
  };

  const onCopyLink = (catalogue) => {
    let domain = "";
    if (window.location.hostname === "localhost") {
      domain = "http://localhost:3000";
    } else {
      domain = `https://${window.location.hostname}`;
    }
    // navigator.clipboard.writeText(
    //   `${domain}/ext/preview?bid=${encodeURIComponent(bookingID)}`
    // );
    copyToClipboard(
      `${domain}/catalogue/viewer?ctgid=${encodeURIComponent(catalogue.id)}`
    );
    toast.success(t("copied_invite_link_message", { name: catalogue.name, interpolation: { escapeValue: false } }));
  };

  // Handle "Edit Articles" — route to section builder or product picker
  const onEditArticlesForCatalogue = useCallback(
    async (catalogue) => {
      if (catalogue.groupingMode === "custom") {
        // Fetch fresh from Firestore to get current articleIDs & customSections
        const freshCatalogue = await getCatalogueByID(catalogue.id);
        const cat = freshCatalogue || catalogue;
        // Open section builder
        setCatalogueForSectionBuilder(cat);
        setSectionBuilderSections(cat.customSections || []);
        const params = new URLSearchParams(window.location.search);
        params.set("builderId", cat.id);
        setSearchParams(params, { replace: true });
        // Hydrate articles
        const entries = normalizeArticleEntries(cat.articleIDs, cat.seasonKey);
        setCatalogueEntries(entries);
        if (cat.articleIDs?.length > 0) {
          const uniqueIDs = extractArticleIDs(entries);
          const articles = await retrieveArticlesByIDs(uniqueIDs);
          setSectionBuilderArticles(Object.values(articles));
        } else {
          setSectionBuilderArticles([]);
        }
      } else {
        // Open product picker
        setCatalogueForPrductPickerModal(catalogue);
      }
    },
    [retrieveArticlesByIDs, setSearchParams, getCatalogueByID]
  );

  const onUpdateSections = useCallback(
    (updatedSections) => {
      setSectionBuilderSections(updatedSections);
      if (catalogueForSectionBuilder) {
        updateCatalogueCustomSections(
          updatedSections,
          catalogueForSectionBuilder.id
        );
      }
    },
    [catalogueForSectionBuilder, updateCatalogueCustomSections]
  );

  const onCloseSectionBuilder = () => {
    // Sync updated sections & articles back into the catalogue list so the
    // cards and template picker reflect changes without a page refresh.
    if (catalogueForSectionBuilder) {
      setFilteredCatalogues((prev) =>
        prev.map((c) =>
          c.id === catalogueForSectionBuilder.id
            ? { ...c, customSections: sectionBuilderSections, articleIDs: catalogueEntries }
            : c
        )
      );
    }
    setCatalogueForSectionBuilder(null);
    setSectionBuilderSections([]);
    setSectionBuilderArticles([]);
    setCatalogueEntries([]);
    const params = new URLSearchParams(window.location.search);
    params.delete("builderId");
    setSearchParams(params, { replace: true });
  };

  const refreshSectionBuilderArticles = useCallback(
    (pickerArticles, groupArticleIDs, entries, removedArticleIDs) => {
      if (!catalogueForSectionBuilder) return;
      // An empty array is legitimate now that the picker can remove: it means the rep emptied the
      // catalogue. Only a missing value means "nothing to apply".
      if (!pickerArticles) return;

      setSectionBuilderArticles(pickerArticles);
      if (entries) setCatalogueEntries(entries);

      if (groupArticleIDs && sectionForNewArticles) {
        // Custom mode: the picker tells us exactly which articles belong in the group, and which
        // it removed from the catalogue outright. Both land in one sections write.
        let updated = updateSectionById(
          sectionBuilderSections,
          sectionForNewArticles.id,
          (s) => ({ ...s, articleIDs: groupArticleIDs })
        );
        if (removedArticleIDs?.length > 0) {
          updated = stripArticleIDsFromSections(updated, removedArticleIDs);
        }
        setSectionBuilderSections(updated);
        updateCatalogueCustomSections(updated, catalogueForSectionBuilder.id);
      } else {
        // Schema mode: auto-assign only new-to-catalogue articles
        const oldIDs = new Set(sectionBuilderArticles.map((a) => a.id));
        const newArticleIDs = pickerArticles
          .map((a) => a.id)
          .filter((id) => !oldIDs.has(id));

        if (newArticleIDs.length > 0 && sectionForNewArticles) {
          const updated = updateSectionById(
            sectionBuilderSections,
            sectionForNewArticles.id,
            (s) => ({ ...s, articleIDs: [...(s.articleIDs || []), ...newArticleIDs] })
          );
          setSectionBuilderSections(updated);
          updateCatalogueCustomSections(updated, catalogueForSectionBuilder.id);
        }
      }
      setSectionForNewArticles(null);
    },
    [
      catalogueForSectionBuilder, sectionBuilderArticles,
      sectionForNewArticles, sectionBuilderSections,
      updateCatalogueCustomSections
    ]
  );

  // Which OTHER sections hold each article, so the picker can warn before a removal reaches a
  // section the rep is not looking at. The group being edited is excluded — its own membership
  // is not news.
  const otherSectionNamesByArticleID = useMemo(
    () =>
      buildSectionNamesByArticleID(sectionBuilderSections, {
        excludeSectionId: sectionForNewArticles?.id
      }),
    [sectionBuilderSections, sectionForNewArticles]
  );

  const onRemoveArticlesFromCatalogue = useCallback(
    (articleIDsToRemove) => {
      if (!catalogueForSectionBuilder) return;
      const idsToRemove = new Set(articleIDsToRemove);
      // Remove from local articles state
      setSectionBuilderArticles((prev) =>
        prev.filter((a) => !idsToRemove.has(a.id))
      );
      // Also remove from any custom sections that might reference them
      const updatedSections = stripArticleIDsFromSections(
        sectionBuilderSections,
        idsToRemove
      );
      setSectionBuilderSections(updatedSections);
      // Update catalogue articleIDs in Firestore (entries-aware)
      const newEntries = catalogueEntries.filter((e) => !idsToRemove.has(e.id));
      setCatalogueEntries(newEntries);
      // Single combined write instead of two independent debounced writes
      updateCatalogueSectionsAndArticles(
        updatedSections, newEntries, catalogueForSectionBuilder.id
      );
    },
    [catalogueForSectionBuilder, catalogueEntries, sectionBuilderSections,
     updateCatalogueSectionsAndArticles]
  );

  const onConfirmCatalogueDeletion = async () => {
    setIsLoading(true);
    await deleteCatalogue(catalogueToDelete.id);
    setFilteredCatalogues((prev) =>
      prev.filter((catalogue) => catalogue.id !== catalogueToDelete.id)
    );
    setIsLoading(false);
    setCatalogueToDelete();
  };

  return (
    <>
      <Loading isLoading={isLoading} />
      {catalogueForSectionBuilder != null ? (
        <CatalogueSectionBuilder
          catalogue={catalogueForSectionBuilder}
          customSections={sectionBuilderSections}
          onUpdateSections={onUpdateSections}
          allArticles={sectionBuilderArticles}
          seasonMap={buildSeasonMap(catalogueEntries)}
          catalogues={filteredCatalogues}
          onOpenProductPicker={(section) => {
            setSectionForNewArticles(
              section && section.id !== "__unassigned__" ? section : null
            );
            setCatalogueForPrductPickerModal(catalogueForSectionBuilder);
          }}
          onBack={onCloseSectionBuilder}
          onRefreshArticles={refreshSectionBuilderArticles}
          onRemoveArticlesFromCatalogue={onRemoveArticlesFromCatalogue}
        />
      ) : (
        <Container>
          <Header>
            <SearchSection>
              <StyledSearchBar
                placeholder={`${t("search")}...`}
                searchTermChanged={onSearchBarChanged}
              />
              <StyledButton
                primary
                fontsize='small'
                onClick={() => setShowCreateCatalogueModal(true)}
              >
                {`+ ${t("create_catalogue")}`}
              </StyledButton>
            </SearchSection>
            <SortSection>
              {`${t("sort")}:`}
              <MiniButton
                highlighted={sortBy === "date"}
                onClick={() => setSortBy("date")}
              >
                {t("date")}
              </MiniButton>
              <MiniButton
                highlighted={sortBy === "alpha"}
                onClick={() => setSortBy("alpha")}
              >
                {t("name_title")}
              </MiniButton>
            </SortSection>
          </Header>
          <StyledScrollArea>
            {filteredCatalogues.map((catalogue) => (
              <CatalogueCard
                key={catalogue.id}
                catalogue={catalogue}
                onEditArticlesForCatalogue={onEditArticlesForCatalogue}
                onEditCatalogueDetails={setCatalogueToEdit}
                onCopyLink={onCopyLink}
                onDeleteCatalogue={setCatalogueToDelete}
              />
            ))}
          </StyledScrollArea>
        </Container>
      )}
      <CreateEditCatalogueModal
        title={
          catalogueToEdit == null ? t("new_catalogue") : t("edit_catalogue")
        }
        catalogue={catalogueToEdit}
        catalogues={filteredCatalogues}
        show={showCreateCatalogueModal || catalogueToEdit != null}
        onClose={() => {
          setShowCreateCatalogueModal(false);
          setCatalogueToEdit();
        }}
        onCreateUpdateCatalogue={onCreateUpdateCatalogue}
      />
      <CatalogueProductPickerModal
        show={catalogueForProductPickerModal != null}
        onClose={(
          pickerArticles,
          groupArticleIDs,
          pickerEntries,
          removedArticleIDs
        ) => {
          setCatalogueForPrductPickerModal();
          if (catalogueForSectionBuilder && pickerArticles) {
            refreshSectionBuilderArticles(
              pickerArticles,
              groupArticleIDs,
              pickerEntries,
              removedArticleIDs
            );
          } else if (pickerEntries) {
            setCatalogueEntries(pickerEntries);
          }
        }}
        catalogueID={catalogueForProductPickerModal?.id}
        isCustomMode={!!catalogueForSectionBuilder}
        targetGroupArticleIDs={sectionForNewArticles?.articleIDs}
        targetGroupName={sectionForNewArticles?.name}
        otherSectionNamesByArticleID={otherSectionNamesByArticleID}
      />
      <ConfirmModal
        show={catalogueToDelete != null}
        onClose={() => setCatalogueToDelete()}
        title={t("delete_catalogue")}
        message={t("confirm_catalogue_delete", {
          name: catalogueToDelete?.name
        })}
        isDestructive
        buttonTitle={t("delete_button")}
        onConfirm={onConfirmCatalogueDeletion}
      />
    </>
  );
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: grid;
  grid-template-rows: auto 1fr;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  padding: 5px;
  border-radius: ${CONSTANTS.borderRadius} ${CONSTANTS.borderRadius} 0 0;
  border-bottom: 2px solid ${COLORS.secondary};
  background: ${COLORS.tertiary};
  box-shadow: 0px 2px 5px ${COLORS.secondary};
  z-index: 1;
`;

const SearchSection = styled.div`
  display: flex;
  align-items: center;
`;

const StyledSearchBar = styled(SearchBar)`
  min-width: 325px;
`;

const StyledButton = styled(Button)`
  margin-left: 10px;
  text-transform: uppercase;
`;

const SortSection = styled.div`
  display: flex;
  align-items: center;
  color: ${COLORS.text};
  font-weight: ${FONTWEIGHT.heavy};
  font-size: ${FONTSIZE.small};
  text-transform: uppercase;
  padding: 10px 5px 5px;

  button {
    margin-left: 10px;
  }
`;

const StyledScrollArea = styled(ScrollArea)`
  grid-template-columns: repeat(auto-fit, minmax(400px, 0.3333fr));
`;

export default CatalogueCreatorTab;
